// useNodeOperations.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import {
  updateAttributeVisibility,
  updateAttributeValue,
  setNodesActionCreator,
  getNextSuggestedName,
} from '../redux/reducers/global/node/nodeSlice';
import { applyNodeChanges } from 'reactflow';

let cloneIdCounter = 0;

const createUniqueCloneId = (existingIds = new Set()) => {
  const ids = existingIds instanceof Set ? existingIds : new Set();
  let id = null;
  do {
    cloneIdCounter += 1;
    const randomPart = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 12);
    id = `clone_${Date.now()}_${cloneIdCounter}_${randomPart}`;
  } while (ids.has(id));
  ids.add(id);
  return id;
};

const sanitizeNodeForStore = (node) => {
  if (!node || typeof node !== 'object') return node;
  const clone = JSON.parse(JSON.stringify(node));
  delete clone.selected;
  delete clone.dragging;
  delete clone.positionAbsolute;
  delete clone.style;
  return clone;
};

const snapshotNode = (node) => {
  if (!node || typeof node !== 'object') return node;
  return sanitizeNodeForStore(node);
};

const cloneNodes = (nodes) => (Array.isArray(nodes) ? nodes.map(sanitizeNodeForStore) : []);
const snapshotNodes = (nodes) => (Array.isArray(nodes) ? nodes.map(snapshotNode) : []);

export const useNodeOperations = (initialNodes = []) => {
  const dispatch = useDispatch();
  const reduxNodes = useSelector((state) => state.node.nodes);
  const selectedNodeIds = useSelector((state) => state.node.selectedNodeIds);
  const gridGapPx = useSelector((state) => state.view.gridGapPx) || 75;
  const GRID_GAP = Math.max(5, Math.min(200, Number(gridGapPx) || 25));

  const [rfNodes, setRfNodes] = useState(initialNodes);
  const reduxSnapshotRef = useRef('[]');
  const dispatchedSnapshotRef = useRef('[]');
  const latestRfSnapshotRef = useRef('[]');

  const rememberLatestRfSnapshot = useCallback((nodes) => {
    try {
      latestRfSnapshotRef.current = JSON.stringify(snapshotNodes(nodes));
    } catch {
      latestRfSnapshotRef.current = '[]';
    }
  }, []);

  const setNodes = useCallback((update) => {
    if (typeof update !== 'function') {
      rememberLatestRfSnapshot(update);
      setRfNodes(update);
      return;
    }
    setRfNodes((previousNodes) => {
      const nextNodes = update(previousNodes);
      rememberLatestRfSnapshot(nextNodes);
      return nextNodes;
    });
  }, [rememberLatestRfSnapshot]);

  // Keep React Flow local state in sync with Redux nodes (when they truly change)
  useEffect(() => {
    const sourceNodes = Array.isArray(reduxNodes) ? reduxNodes : [];
    const selectedSet = new Set(Array.isArray(selectedNodeIds) ? selectedNodeIds : []);
    const nodesWithSelection = sourceNodes.map((node) => ({
      ...node,
      selected: selectedSet.has(node.id),
    }));
    const snapshot = snapshotNodes(sourceNodes);
    const selectionKey = Array.from(selectedSet).sort().join('|');
    let serialized = '[]';
    try {
      serialized = JSON.stringify({ nodes: snapshot, selection: selectionKey });
    } catch {
      serialized = '[]';
    }
    if (serialized !== reduxSnapshotRef.current) {
      setNodes(nodesWithSelection);
      reduxSnapshotRef.current = serialized;
    }
  }, [reduxNodes, selectedNodeIds, setNodes]);

  // Push React Flow node state back into Redux when it changes materially
  useEffect(() => {
    const flushToRedux = debounce((nodesToFlush) => {
      const cloned = cloneNodes(nodesToFlush);
      const snapshot = snapshotNodes(nodesToFlush);
      let serialized = '[]';
      try {
        serialized = JSON.stringify(snapshot);
      } catch {
        serialized = '[]';
      }
      if (serialized !== latestRfSnapshotRef.current) {
        return;
      }
      if (serialized !== dispatchedSnapshotRef.current) {
        dispatch(setNodesActionCreator({ nodes: cloned }));
        dispatchedSnapshotRef.current = serialized;
      }
    }, 16); // roughly one frame

    try {
      latestRfSnapshotRef.current = JSON.stringify(snapshotNodes(rfNodes));
    } catch {
      latestRfSnapshotRef.current = '[]';
    }
    flushToRedux(rfNodes);
    return () => flushToRedux.cancel();
  }, [rfNodes, dispatch]);

  const handleNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, [setNodes]);

  const handleVisibilityChange = useCallback(
    (nodeId, attributeName, isVisible) => {
      setNodes((nds) => nds.map((node) => {
        if (node.id !== nodeId) return node;
        const attributes = node.data?.attributes;
        if (!attributes || !attributes[attributeName]) {
          return node;
        }
        return {
          ...node,
          data: {
            ...(node.data || {}),
            attributes: {
              ...attributes,
              [attributeName]: {
                ...attributes[attributeName],
                isVisible,
              },
            },
          },
        };
      }));
      dispatch(updateAttributeVisibility({ nodeId, attributeName, isVisible }));
    },
    [dispatch, setNodes],
  );

  const handleValueChange = useCallback(
    (nodeId, attributeName, value) => {
      setNodes((nds) => nds.map((node) => {
        if (node.id !== nodeId) return node;
        const attributes = node.data?.attributes;
        if (!attributes || !attributes[attributeName]) {
          return node;
        }
        return {
          ...node,
          data: {
            ...(node.data || {}),
            attributes: {
              ...attributes,
              [attributeName]: {
                ...attributes[attributeName],
                value,
              },
            },
          },
        };
      }));
      dispatch(updateAttributeValue({ nodeId, attributeName, value }));
    },
    [dispatch, setNodes],
  );

  const cloneNode = useCallback((nodeToClone, reservedIds = new Set(), reservedNames = new Set()) => {
    if (!nodeToClone) return null;
    const source = JSON.parse(JSON.stringify(nodeToClone));
    const {
      positionAbsolute,
      dragging,
      selected,
      ...rest
    } = source;

    const dataClone = { ...(source.data || {}) };
    if (Array.isArray(source.data?.handles)) {
      dataClone.handles = source.data.handles.map((handle) => ({
        ...handle,
        style: handle?.style ? { ...handle.style } : undefined,
      }));
    }
    if (Array.isArray(source.data?.variants)) {
      dataClone.variants = source.data.variants.map((variant) => ({ ...variant }));
    }
    if (source.data?.attributes) {
      try {
        dataClone.attributes = JSON.parse(JSON.stringify(source.data.attributes));
      } catch {
        dataClone.attributes = { ...(source.data.attributes || {}) };
      }
    }

    const symbolLabel = (dataClone && dataClone.symbolLabel) || source.data?.symbolLabel;

    const offsetX = Math.max(GRID_GAP, 200);
    const baseX = (source.position?.x || 0) + offsetX;
    const baseY = source.position?.y || 0;

    const clonedNode = {
      ...rest,
      id: createUniqueCloneId(new Set([
        ...(Array.isArray(reduxNodes) ? reduxNodes.map((node) => node?.id).filter(Boolean) : []),
        ...(reservedIds instanceof Set ? Array.from(reservedIds) : []),
      ])),
      data: dataClone,
      position: {
        x: Math.round(baseX / GRID_GAP) * GRID_GAP,
        y: Math.round(baseY / GRID_GAP) * GRID_GAP,
      },
      positionAbsolute: undefined,
      dragging: false,
      selected: false,
    };

    if (symbolLabel) {
      const sanitizedReduxNodes = snapshotNodes(reduxNodes);
      const cloneSeedName =
        dataClone?.attributes?.nombre?.value
        || dataClone?.attributes?.name?.value
        || dataClone?.name
        || source.data?.label
        || null;
      const nextName = getNextSuggestedName(
        symbolLabel,
        sanitizedReduxNodes,
        dataClone?.alias || dataClone?.baseAlias || dataClone?.variantAlias,
        cloneSeedName,
        reservedNames,
      );
      if (nextName) {
        clonedNode.data = clonedNode.data || {};
        clonedNode.data.attributes = clonedNode.data.attributes || {};
        const nombreAttr = clonedNode.data.attributes.nombre;
        if (nombreAttr) {
          clonedNode.data.attributes.nombre = {
            ...nombreAttr,
            value: nextName,
          };
        } else {
          clonedNode.data.attributes.nombre = {
            isVisible: true,
            label: 'Nombre',
            value: nextName,
          };
        }
      }
    }

    return clonedNode;
  }, [GRID_GAP, reduxNodes]);

  return {
    nodes: rfNodes,
    setNodes,
    cloneNode,
    handleNodesChange,
    handleVisibilityChange,
    handleValueChange,
  };
};
